import React, { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  emailValidator,
  MobileNoIntValidator,
  requiredValidator,
} from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";

import { closed } from "../../components/drawer/drawerSlice";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { clearLeadDetails } from "./leadSlice";
import {
  createLead,
  getAllLeads,
  getLeadByID,
  updateLead,
} from "./services/lead.services";
import IconButton from "../../components/common/IconButton";
import { getAllActiveLeadSource } from "../leadsource/services/leadsource.service";
import { getAllActiveLeadStage } from "../leadstage/services/leadStage.services";
import { ILeadStage } from "../leadstage/leadStageModel";
import { ILeadSource } from "../leadsource/leadsourceModel";
import { NAMEPREFIX } from "../../_contstants/common";
import CoutryCodeField from "../../components/formFields/CountryCodeField";
import { useLocation } from "react-router-dom";
import { getDashboardLeads } from "../dashboard/services/dashboard.services";
import { getContactByID } from "../contact/services/contact.services";

const CreateLead: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const ContactID = location.state?.ContactID;
  const gridRef = useRef<any>(null);

  const LeadDetails = useAppSelector((state) => state.lead.LeadDetails);
  const LeadID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.lead.formLoading);
  const LeadFilterDetails = useAppSelector(
    (state) => state.lead.LeadFilterDetails
  );
  const LeadSourceList = useAppSelector(
    (state) => state.leadsource.LeadSourceList
  );
  const LeadStageList = useAppSelector(
    (state) => state.leadStage.LeadStageList
  );
  const ContactDetails = useAppSelector(
    (state) => state.contact.ContactDetails
  );
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const dateFilter = useAppSelector((state) => state.dashboard.dateFilter);

  const [formKey, setFormKey] = useState(1);
  const [initialValues, setInitialValues] = useState<any>({});

  useEffect(() => {
    dispatch(getAllActiveLeadSource());
    dispatch(getAllActiveLeadStage("LEAD"));
    if (!LeadID && ContactID) {
      dispatch(getContactByID(ContactID));
    }

    return () => {
      dispatch(clearLeadDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    if (LeadID) {
      dispatch(getLeadByID(LeadID));
    }
  }, [LeadID]);

  useEffect(() => {
    if (ContactDetails?.ID) {
      setInitialValues({
        Initial: ContactDetails?.Initial,
        FirstName: ContactDetails?.FirstName,
        LastName: ContactDetails?.LastName,
        MobileNo: ContactDetails?.MobileNo,
        EmailID: ContactDetails?.EmailID,
        CountryCode: ContactDetails?.CountryCode,
        LeadSourceID: ContactDetails?.LeadSourceID,
        ContactID: ContactDetails?.ID,
      });
    }
  }, [ContactDetails]);

  useEffect(() => {
    setInitialValues(LeadDetails);
  }, [LeadDetails]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [initialValues]);

  const handleSubmit = async (values: any) => {
    try {
      if (LeadID) {
        const editPayload = {
          ID: LeadID,
          Initial: values?.Initial ? values?.Initial : "",
          FirstName: values?.FirstName ? values?.FirstName : "",
          LastName: values?.LastName ? values?.LastName : "",
          MobileNo: values?.MobileNo ? values?.MobileNo : "",
          CountryCode: values?.CountryCode ? values?.CountryCode : "",
          EmailID: values?.EmailID ? values?.EmailID : "",
          LeadSourceID: values?.LeadSourceID ? +values?.LeadSourceID : null,
          LeadStageID: values?.LeadStageID ? +values?.LeadStageID : null,
        };
        const response = await dispatch(updateLead(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearLeadDetails());
          if (location.pathname === "/dashboard/lead") {
            const payload = {
              FromDate: dateFilter?.FromDate ? dateFilter?.FromDate : "",
              ToDate: dateFilter?.ToDate ? dateFilter?.ToDate : "",
            };
            dispatch(getDashboardLeads(payload));
          } else {
            dispatch(
              getAllLeads({
                ...LeadFilterDetails,
                limit: +pageLimit,
                skip: +currentPage * +pageLimit,
              })
            );
          }
        }
      } else {
        const insertPayload = {
          Initial: values?.Initial ? values?.Initial : "",
          FirstName: values?.FirstName ? values?.FirstName : "",
          LastName: values?.LastName ? values?.LastName : "",
          MobileNo: values?.MobileNo ? values?.MobileNo : "",
          CountryCode: values?.CountryCode ? values?.CountryCode : "",
          EmailID: values?.EmailID ? values?.EmailID : "",
          LeadSourceID: values?.LeadSourceID ? +values?.LeadSourceID : null,
          LeadStageID: values?.LeadStageID ? +values?.LeadStageID : null,
        };
        const response = await dispatch(createLead(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearLeadDetails());
          dispatch(
            getAllLeads({
              ...LeadFilterDetails,
              limit: +pageLimit,
              skip: +currentPage * +pageLimit,
            })
          );
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
    dispatch(clearLeadDetails());
  };

  if (formLoading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <Form
      key={formKey}
      onSubmit={handleSubmit}
      initialValues={initialValues}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement style={{ width: "100%" }}>
          <GridLayout style={{}} gap={{ rows: 10 }} cols={[{ width: "100%" }]}>
            <GridLayoutItem className="mui-drawer-header">
              <Typography.h4 className="m-0">
                {LeadID ? "Update Lead" : "Add Lead"}
              </Typography.h4>
              <IconButton
                type="button"
                fillMode={"flat"}
                themeColor={"dark"}
                style={{ marginRight: 4 }}
                onClick={() => dispatch(closed())}
                icon={RxCross1}
              />
            </GridLayoutItem>
            <GridLayoutItem className="d-flex gap-2">
              <Field
                wrapperStyle={{
                  width: "100%",
                  //   marginTop: 34,
                  flex: 1,
                }}
                id={"Initial"}
                name={"Initial"}
                label={"Initial"}
                placeholder="i.e. Mr."
                component={FormSelectionField}
                options={NAMEPREFIX?.map((initial: any) => {
                  return {
                    value: initial?.value,
                    label: initial?.label,
                  };
                })}
              />
              <Field
                wrapperStyle={{
                  width: "100%",
                  flex: 2,
                }}
                name="FirstName"
                label="First Name"
                placeholder="i.e.John"
                maxLength="100"
                component={FormTextField}
                validator={requiredValidator}
                astrike={true}
              />
            </GridLayoutItem>
            <GridLayoutItem>
              <Field
                name="LastName"
                label="Last Name"
                placeholder="i.e. Smith"
                maxLength="100"
                component={FormTextField}
                validator={requiredValidator}
                astrike={true}
              />
            </GridLayoutItem>
            <GridLayoutItem className="d-flex gap-2">
              <Field
                wrapperStyle={{
                  width: "100%",
                  flex: 1,
                }}
                name="CountryCode"
                label="Code"
                astrike={true}
                placeholder="i.e. In"
                component={CoutryCodeField}
                validator={requiredValidator}
              />
              <Field
                wrapperStyle={{
                  width: "100%",
                  flex: 2,
                }}
                name="MobileNo"
                label="Mobile No."
                type="number"
                max={16}
                placeholder="i.e. 9999999999"
                component={FormTextField}
                astrike={true}
                validator={MobileNoIntValidator}
              />
            </GridLayoutItem>
            <GridLayoutItem>
              <Field
                name="EmailID"
                label="Email ID"
                astrike={true}
                placeholder="i.e. example@gmail.com"
                component={FormTextField}
                validator={emailValidator}
              />
            </GridLayoutItem>
            <GridLayoutItem>
              <Field
                wrapperClassName="w-100"
                id={"LeadSourceID"}
                name={"LeadSourceID"}
                label={"Lead Source"}
                placeholder="Lead Source"
                component={FormSelectionField}
                astrike={true}
                validator={requiredValidator}
                options={LeadSourceList?.map((leadsource: ILeadSource) => {
                  return {
                    value: leadsource?.ID,
                    label: leadsource?.Source,
                  };
                })}
              />
            </GridLayoutItem>
            <GridLayoutItem>
              <Field
                wrapperClassName="w-100"
                id={"LeadStageID"}
                name={"LeadStageID"}
                label={"Lead Stage"}
                placeholder="Lead Stage"
                component={FormSelectionField}
                // astrike={true}
                // validator={requiredValidator}
                options={LeadStageList?.map((leadstage: ILeadStage) => {
                  return {
                    value: leadstage?.ID,
                    label: leadstage?.LeadStage,
                  };
                })}
              />
            </GridLayoutItem>

            <GridLayoutItem
              style={{
                textAlign: "end",
                marginTop: "20px",
                width: "100%",
              }}
            >
              <ButtonWithLoading
                label={LeadID ? "Update" : "Save"}
                type="submit"
                disabled={!formRenderProps.allowSubmit || formLoading}
                loading={formLoading}
              />
              <Button
                type="button"
                fillMode={"outline"}
                themeColor={"primary"}
                style={{ marginLeft: 10 }}
                onClick={handleFormClose}
              >
                Cancel
              </Button>
            </GridLayoutItem>
          </GridLayout>
        </FormElement>
      )}
    />
  );
};

export default CreateLead;
